"use strict";

import { track as trackPublicEvent } from "./track";
import { getStoredRedditClickId } from "./campaignAttribution";

/**
 * Reddit Ads pixel ID (public, same pattern as the GA tag in renderFullPage).
 * Events Manager → Reddit Pixel.
 */
export const REDDIT_PIXEL_ID = "a2_itpf4rgodvqb";

export const REDDIT_REDIRECT_PARAM = "rdt";
export const REDDIT_STEAM_LOGIN = "steam_login";
export const REDDIT_STEAM_SIGNUP = "steam_signup";
export const REDDIT_CONVERSION_ID_PARAM = "rdtcid";
export const REDDIT_SIGNUP_CONVERSION_ID_PARAM = "rdtsid";

const ACCOUNT_PROFILE_STORAGE_KEY = "rdt_account_profile";

const STANDARD_EVENTS = new Set([
  "PageVisit",
  "ViewContent",
  "Search",
  "AddToCart",
  "AddToWishlist",
  "Purchase",
  "Lead",
  "SignUp",
]);

function getRdt() {
  if (typeof window === "undefined") return null;
  if (typeof window.rdt !== "function") return null;
  return window.rdt;
}

export function createRedditConversionId() {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch (_) {
    // Fall through to a compact unique string.
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

export function trackRedditEvent(eventName, extra) {
  try {
    const rdt = getRdt();
    if (!rdt || !eventName) return;

    const payload =
      extra && typeof extra === "object" && Object.keys(extra).length > 0 ? extra : null;

    if (STANDARD_EVENTS.has(eventName)) {
      if (payload) {
        rdt("track", eventName, payload);
      } else {
        rdt("track", eventName);
      }
      return;
    }

    rdt("track", "Custom", {
      customEventName: eventName,
      ...(payload || {}),
    });
  } catch (_) {
    // Tracking must never crash a component.
  }
}

export function trackRedditGoogleSignup(isNewUser, conversionId) {
  if (isNewUser === true) {
    trackRedditEvent("SignUp", conversionId ? { conversionId } : undefined);
  }
}

export function getPendingAccountProfileLeadConversion() {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      if (window.localStorage.getItem(ACCOUNT_PROFILE_STORAGE_KEY)) {
        return null;
      }
    }
  } catch (_) {
    // Still fire if storage is unavailable so the first save is not dropped.
  }
  return createRedditConversionId();
}

export function trackRedditAccountProfile(conversionId) {
  if (!conversionId) return;
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.setItem(ACCOUNT_PROFILE_STORAGE_KEY, conversionId);
    }
  } catch (_) {
    // Pixel fire still proceeds if storage is unavailable.
  }
  trackRedditEvent("Lead", { conversionId });
}

export function isDiscordInviteUrl(href) {
  if (!href || typeof href !== "string") return false;
  try {
    const url = new URL(href, "https://www.ouro.is");
    const host = url.hostname.replace(/^www\./, "").toLowerCase();
    if (host === "discord.gg") return url.pathname.length > 1;
    if (host === "discord.com" || host === "discordapp.com") {
      return url.pathname.startsWith("/invite/");
    }
    return false;
  } catch (_) {
    return false;
  }
}

export function consumeRedditRedirectConversion(search) {
  try {
    const params =
      typeof search === "string" ? new URLSearchParams(search) : search;
    const conversion = params.get(REDDIT_REDIRECT_PARAM);
    if (conversion !== REDDIT_STEAM_LOGIN && conversion !== REDDIT_STEAM_SIGNUP) {
      return null;
    }
    const steamLoginConversionId = params.get(REDDIT_CONVERSION_ID_PARAM) || null;
    const signUpConversionId = params.get(REDDIT_SIGNUP_CONVERSION_ID_PARAM) || null;
    params.delete(REDDIT_REDIRECT_PARAM);
    params.delete(REDDIT_CONVERSION_ID_PARAM);
    params.delete(REDDIT_SIGNUP_CONVERSION_ID_PARAM);
    return {
      conversion,
      steamLoginConversionId,
      signUpConversionId,
      nextSearch: params.toString(),
    };
  } catch (_) {
    return null;
  }
}

function fireSteamAuthConversion(conversion, conversionIds = {}) {
  try {
    if (typeof window !== "undefined" && window.sessionStorage) {
      const key = `rdt_${conversion}`;
      if (window.sessionStorage.getItem(key)) return;
      window.sessionStorage.setItem(key, "1");
    }
  } catch (_) {
    // Best-effort dedupe only.
  }
  trackRedditEvent(
    "SteamLogin",
    conversionIds.steamLoginConversionId
      ? { conversionId: conversionIds.steamLoginConversionId }
      : undefined
  );
  if (conversion === REDDIT_STEAM_SIGNUP) {
    trackRedditEvent(
      "SignUp",
      conversionIds.signUpConversionId
        ? { conversionId: conversionIds.signUpConversionId }
        : undefined
    );
  }
}

function pingDiscordInviteClick({ conversionId, addToCartConversionId }) {
  try {
    trackPublicEvent("discord_invite_clicked", {
      meta: {
        conversionId,
        addToCartConversionId,
        redditClickId: getStoredRedditClickId(),
      },
    });
  } catch (_) {
    // Best-effort only.
  }
}

export function buildRedditPixelSnippet(cspNonce = "") {
  const nonceAttr = cspNonce ? ` nonce="${cspNonce}"` : "";
  return `
        <!-- Reddit Pixel -->
        <script${nonceAttr}>
          !function(w,d){if(!w.rdt){var p=w.rdt=function(){p.sendEvent?p.sendEvent.apply(p,arguments):p.callQueue.push(arguments)};p.callQueue=[]};var t=d.createElement("script");t.src="https://www.redditstatic.com/ads/pixel.js",t.async=!0;var s=d.getElementsByTagName("script")[0];s.parentNode.insertBefore(t,s)}(window,document);rdt('init','${REDDIT_PIXEL_ID}');rdt('track','PageVisit');
        </script>
`;
}

export function installRedditPixelClientTracking({
  search,
  pathname,
  hash,
  replaceSearch,
} = {}) {
  if (typeof document === "undefined") return undefined;

  const consumed = consumeRedditRedirectConversion(search || "");
  if (consumed) {
    fireSteamAuthConversion(consumed.conversion, {
      steamLoginConversionId: consumed.steamLoginConversionId,
      signUpConversionId: consumed.signUpConversionId,
    });
    if (typeof replaceSearch === "function") {
      replaceSearch({
        pathname: pathname || "/",
        search: consumed.nextSearch ? `?${consumed.nextSearch}` : "",
        hash: hash || "",
      });
    }
  }

  function onDocumentClick(event) {
    const anchor = event.target && event.target.closest && event.target.closest("a[href]");
    if (!anchor) return;
    const href = anchor.getAttribute("href") || anchor.href;
    if (!isDiscordInviteUrl(href)) return;
    const conversionId = createRedditConversionId();
    const addToCartConversionId = createRedditConversionId();
    trackRedditEvent("DiscordClick", { conversionId });
    trackRedditEvent("AddToCart", { conversionId: addToCartConversionId });
    pingDiscordInviteClick({ conversionId, addToCartConversionId });
  }

  document.addEventListener("click", onDocumentClick, true);
  return () => {
    document.removeEventListener("click", onDocumentClick, true);
  };
}
